import React, { useEffect, useState } from 'react';

const DISMISS_KEY = 'pwa_install_dismissed_until';
const DISMISS_DAYS = 14;

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isAndroid() {
  return /android/i.test(navigator.userAgent);
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('desktop'); // 'android' | 'ios' | 'desktop'

  useEffect(() => {
    if (isStandalone()) return;

    const dismissedUntil = localStorage.getItem(DISMISS_KEY);
    if (dismissedUntil && Date.now() < Number(dismissedUntil)) return;

    if (isIOS()) {
      setPlatform('ios');
      setVisible(true);
      return;
    }

    if (isAndroid()) {
      setPlatform('android');
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setVisible(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }

    // Desktop — do not show the install prompt
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now() + DISMISS_DAYS * 864e5);
    setVisible(false);
  };

  if (!visible) return null;

  const subtitle = {
    ios:     'Tap the Share icon ↗ then "Add to Home Screen" to install.',
    android: 'Add to your home screen for a faster, app-like experience.',
    desktop: 'Click the install icon ⊕ in your browser\'s address bar to install.',
  }[platform];

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      background: 'linear-gradient(135deg, #fff 0%, #FDF0F6 100%)',
      borderTop: '2px solid #F0CDDB',
      borderRadius: '20px 20px 0 0',
      boxShadow: '0 -8px 32px rgba(167,44,104,0.18)',
      padding: '18px 20px 22px',
      fontFamily: 'helvetica-lt-std-bold, sans-serif',
    }}>
      {/* drag handle */}
      <div style={{
        width: 36, height: 4, borderRadius: 99,
        background: '#E8C4D8', margin: '0 auto 14px',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* icon */}
        <div style={{
          width: 54, height: 54, borderRadius: 14, flexShrink: 0,
          background: 'linear-gradient(135deg, #C0357A, #A72C68)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(167,44,104,0.35)',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#fff" opacity="0.9"/>
            <path d="M2 17l10 5 10-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12l10 5 10-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {/* text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 3 }}>
            3D Lifestyle Aesthetics
          </div>
          <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.4 }}>
            {subtitle}
          </div>
        </div>

        {/* close */}
        <button onClick={handleDismiss} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 4, color: '#9CA3AF', flexShrink: 0, lineHeight: 1,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {platform === 'android' && deferredPrompt && (
        <button onClick={handleInstall} style={{
          marginTop: 14, width: '100%',
          background: 'linear-gradient(135deg, #C0357A, #A72C68)',
          color: '#fff', border: 'none', borderRadius: 12,
          padding: '13px 0', fontSize: 14, fontWeight: 700,
          cursor: 'pointer', letterSpacing: '0.3px',
          boxShadow: '0 4px 14px rgba(167,44,104,0.4)',
        }}>
          Install App — It&apos;s Free
        </button>
      )}

      <button onClick={handleDismiss} style={{
        marginTop: platform === 'android' && deferredPrompt ? 8 : 14,
        width: '100%', background: 'none', border: 'none',
        color: '#9CA3AF', fontSize: 12, cursor: 'pointer', padding: '6px 0',
      }}>
        Not now
      </button>
    </div>
  );
}
